import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useGalleries } from '../hooks';

const css = `
.nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 100;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 4rem; height: 72px;
  background: var(--dark);
}
.nav-logo { text-decoration: none; line-height: 1.2; }
.nav-logo .logo-main {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.35rem; font-weight: 500;
  color: var(--aqua-light); letter-spacing: 0.04em;
}
.nav-logo .logo-sub {
  display: block;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.6rem; font-weight: 600;
  letter-spacing: 0.2em; text-transform: uppercase;
  color: var(--gold); margin-top: 1px;
}
.nav-links {
  display: flex; align-items: center; gap: 2.5rem; list-style: none;
}
.nav-links a {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.8rem; font-weight: 600;
  letter-spacing: 0.15em; text-transform: uppercase;
  color: rgba(255,255,255,0.7); text-decoration: none;
  transition: color 0.2s;
}
.nav-links a:hover, .nav-links a.active { color: var(--aqua-light); }

/* Gallery dropdown — click-based, flat list */
.gallery-wrapper { position: relative; }
.gallery-trigger {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.8rem; font-weight: 600;
  letter-spacing: 0.15em; text-transform: uppercase;
  color: rgba(255,255,255,0.7); background: none; border: none;
  cursor: pointer; padding: 0; display: flex; align-items: center; gap: 0.3rem;
  transition: color 0.2s;
}
.gallery-trigger:hover,
.gallery-trigger.open { color: var(--aqua-light); }
.gallery-dropdown {
  position: absolute; top: calc(100% + 16px); left: 50%;
  transform: translateX(-50%) translateY(-4px);
  background: white;
  border: 1px solid rgba(141,189,189,0.3);
  border-top: 2px solid var(--gold);
  min-width: 220px;
  box-shadow: 0 8px 32px rgba(55,100,123,0.12);
  opacity: 0; pointer-events: none;
  transition: opacity 0.18s, transform 0.18s;
  z-index: 200;
}
.gallery-dropdown.open {
  opacity: 1; pointer-events: all;
  transform: translateX(-50%) translateY(0);
}
.gallery-dropdown a {
  display: block; padding: 0.6rem 1.25rem;
  font-family: 'Barlow', sans-serif;
  font-size: 0.8rem; letter-spacing: 0.03em;
  color: var(--dark); text-transform: none;
  border-bottom: 1px solid rgba(141,189,189,0.12);
  transition: background 0.15s, color 0.15s;
}
.gallery-dropdown a:last-child { border-bottom: none; }
.gallery-dropdown a:hover { background: var(--off-white); color: var(--aqua-dark); }

/* CTA button */
.nav-cta {
  background: var(--aqua-light) !important;
  color: var(--dark) !important;
  padding: 0.55rem 1.25rem !important;
  transition: background 0.2s !important;
}
.nav-cta:hover { background: var(--gold) !important; color: white !important; }

/* Hamburger button — hidden on desktop */
.nav-hamburger {
  display: none;
  flex-direction: column; justify-content: center; align-items: center;
  gap: 5px; width: 40px; height: 40px;
  background: none; border: none; cursor: pointer; padding: 4px;
}
.nav-hamburger span {
  display: block; width: 24px; height: 2px;
  background: var(--aqua-light);
  transition: transform 0.25s, opacity 0.25s;
  transform-origin: center;
}
.nav-hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.nav-hamburger.open span:nth-child(2) { opacity: 0; }
.nav-hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

/* Mobile overlay menu */
.nav-mobile-overlay {
  display: none;
  position: fixed; top: 72px; left: 0; right: 0; bottom: 0;
  background: rgba(255,255,255,0.98);
  backdrop-filter: blur(8px);
  z-index: 99;
  flex-direction: column;
  padding: 1.5rem 2rem;
  overflow-y: auto;
}
.nav-mobile-overlay.open { display: flex; }
.nav-mobile-overlay > ul {
  list-style: none; display: flex; flex-direction: column; gap: 0;
}
.nav-mobile-overlay > ul > li > a,
.nav-mobile-overlay > ul > li > button {
  display: block; width: 100%;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 1.05rem; font-weight: 600;
  letter-spacing: 0.15em; text-transform: uppercase;
  color: var(--mid); text-decoration: none;
  background: none; border: none; text-align: left; cursor: pointer;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(141,189,189,0.2);
  transition: color 0.2s;
}
.nav-mobile-overlay > ul > li > a:hover,
.nav-mobile-overlay > ul > li > button:hover { color: var(--aqua-dark); }
.nav-mobile-gallery-sub {
  padding: 0.5rem 0 0.5rem 1rem;
  border-bottom: 1px solid rgba(141,189,189,0.2);
}
.nav-mobile-gallery-sub a {
  display: block;
  font-family: 'Barlow', sans-serif;
  font-size: 0.85rem; font-weight: 400;
  letter-spacing: 0.03em; text-transform: none;
  color: var(--mid); text-decoration: none;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(141,189,189,0.1);
  transition: color 0.2s;
}
.nav-mobile-gallery-sub a:last-child { border-bottom: none; }
.nav-mobile-gallery-sub a:hover { color: var(--aqua-dark); }
.nav-mobile-cta {
  margin-top: 1.5rem;
  display: block;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.8rem; font-weight: 700;
  letter-spacing: 0.18em; text-transform: uppercase;
  background: var(--aqua-dark); color: white;
  text-decoration: none; text-align: center;
  padding: 1rem; transition: background 0.2s;
}
.nav-mobile-cta:hover { background: var(--gold); }

@media (max-width: 900px) {
  .nav { padding: 0 1.5rem; }
  .nav-links { display: none; }
  .nav-hamburger { display: flex; }
}
`;

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [mobileGalleryOpen, setMobileGalleryOpen] = useState(false);
  const data = useGalleries();
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Close desktop dropdown when clicking outside
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setGalleryOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
    setGalleryOpen(false);
    setMobileGalleryOpen(false);
  }, [location.pathname]);

  // Scroll listener
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Sort galleries alphabetically by title
  const sortedGalleries = [...data].sort((a, b) => {
    return (a.title || '').localeCompare(b.title || '');
  });

  return (
    <>
      <style>{css}</style>
      <nav className={`nav${scrolled ? ' scrolled' : ''}`}>
        <Link to="/" className="nav-logo">
          <span className="logo-main">Arana Brothers</span>
          <span className="logo-sub">Construction</span>
        </Link>

        {/* Desktop nav links */}
        <ul className="nav-links">
          <li><Link to="/services/repairs">Services</Link></li>
          <li className="gallery-wrapper" ref={dropdownRef}>
            <button
              className={`gallery-trigger${galleryOpen ? ' open' : ''}`}
              onClick={() => setGalleryOpen(o => !o)}
              aria-expanded={galleryOpen}
            >
              Gallery
            </button>
            <div className={`gallery-dropdown${galleryOpen ? ' open' : ''}`}>
              {sortedGalleries.map(g => (
                <Link
                  key={g.slug}
                  to={`/gallery/${g.slug}`}
                  onClick={() => setGalleryOpen(false)}
                >
                  {g.title || g.navLabel || g.slug}
                </Link>
              ))}
            </div>
          </li>
          <li><Link to="/#team">Team</Link></li>
          <li><Link to="/#contact">Contact</Link></li>
          <li><Link to="/#contact" className="nav-cta">Start a Project</Link></li>
        </ul>

        {/* Hamburger — mobile only */}
        <button
          className={`nav-hamburger${menuOpen ? ' open' : ''}`}
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle navigation menu"
          aria-expanded={menuOpen}
        >
          <span />
          <span />
          <span />
        </button>
      </nav>

      {/* Mobile full-screen overlay menu */}
      <div className={`nav-mobile-overlay${menuOpen ? ' open' : ''}`} aria-hidden={!menuOpen}>
        <ul>
          <li><Link to="/services/repairs">Services</Link></li>
          <li>
            <button onClick={() => setMobileGalleryOpen(o => !o)}>
              Gallery
            </button>
            {mobileGalleryOpen && (
              <div className="nav-mobile-gallery-sub">
                {sortedGalleries.map(g => (
                  <Link key={g.slug} to={`/gallery/${g.slug}`}>
                    {g.title || g.navLabel || g.slug}
                  </Link>
                ))}
              </div>
            )}
          </li>
          <li><Link to="/#team">Team</Link></li>
          <li><Link to="/#contact">Contact</Link></li>
        </ul>
        <Link to="/#contact" className="nav-mobile-cta">Start a Project</Link>
      </div>
    </>
  );
}
